import { HttpClient } from '@angular/common/http';
import { loadTranslations } from '@angular/localize';
import { TranslateLoader } from '@ngx-translate/core';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import xliff from 'xliff';

export class TranslationHttpLoader implements TranslateLoader {
    public static readonly WAIT_FOR_SERVER = 3000;
    public static readonly FALLBACK_PREFIX = '/assets/i18n/';
    public static readonly FALLBACK_SUFFIX = '.xlf';
    public static readonly NO_CACHE_HEADERS: Record<string, string> = {
        'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
    };

    constructor(
        private http: HttpClient,
        public prefix: string,
        public fallbackPrefix: string = TranslationHttpLoader.FALLBACK_PREFIX,
        public fallbackSuffix: string = TranslationHttpLoader.FALLBACK_SUFFIX
    ) {}

    public getTranslation(lang: string): Observable<Record<string, string>> {
        const localTranslations = getLocalTranslation(
            this.http,
            lang,
            this.fallbackPrefix,
            this.fallbackSuffix,
            TranslationHttpLoader.NO_CACHE_HEADERS
        ).pipe(catchError(() => of(null)));
        const remoteTranslations = this.http
            .get(buildTranslationUrl(`${this.prefix}${lang}`), {
                responseType: 'text',
                headers: TranslationHttpLoader.NO_CACHE_HEADERS,
            })
            .pipe(
                switchMap((translation) => from(xliff.xliff12ToJs(translation))),
                map((json) => toTranslationFormat(json)),
                timeout(TranslationHttpLoader.WAIT_FOR_SERVER),
                catchError((err) => {
                    if (environment.debug) {
                        console.log(`Translation error: ${this.prefix}${lang}`, err);
                    }

                    return of(null);
                })
            );

        return forkJoin({ localTranslations, remoteTranslations }).pipe(
            map(({ localTranslations, remoteTranslations }) => {
                if (!localTranslations && !remoteTranslations) {
                    throw new Error(`Unable to load translations for ${lang}`);
                }

                return { ...localTranslations, ...remoteTranslations };
            }),
            tap((translations) => loadTranslations(translations))
        );
    }
}

export function getLocalTranslation(
    http: HttpClient,
    lang: string,
    fallbackPrefix: string = TranslationHttpLoader.FALLBACK_PREFIX,
    fallbackSuffix: string = TranslationHttpLoader.FALLBACK_SUFFIX,
    headers: Record<string, string> = TranslationHttpLoader.NO_CACHE_HEADERS
): Observable<Record<string, string>> {
    return http
        .get(buildTranslationUrl(`${fallbackPrefix}${lang}${fallbackSuffix}`), {
            responseType: 'text',
            headers,
        })
        .pipe(
            switchMap((translation) => from(xliff.xliff12ToJs(translation))),
            map((json) => toTranslationFormat(json))
        );
}

function buildTranslationUrl(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cacheBust=${Date.now()}`;
}

function toTranslationFormat(json): Record<string, string> {
    let obj = json.resources;

    //Create an object with the resources
    obj = Object.keys(obj).reduce((acc, key) => {
        return obj[key];
    }, {});

    const translations = Object.keys(obj).reduce<Record<string, string>>((acc, key) => {
        let target = '';
        if (Array.isArray(obj[key].target)) {
            obj[key].target.forEach((targetString) => {
                if (targetString.Standalone) {
                    target += '{{' + targetString.Standalone.id + '}}';
                } else {
                    target += targetString;
                }
            });
        } else {
            target = obj[key].target;
        }

        if (typeof target === 'string' && target.trim()) {
            acc[key] = target;
        }
        return acc;
    }, {});

    return translations;
}

export function TranslationHttpLoaderFactory(httpClient: HttpClient): TranslateLoader {
    return new TranslationHttpLoader(httpClient, environment.apiUrl + '/i18n/mobile/');
}
